import {
  getUserAccountProfileExistStatus,
  getUserExistsAndEligibility,
  UserAccountProfileExistResponse,
  UserExistsResponse,
} from '@/api/api';
import {AxiosResponse} from 'axios';
import {useCallback, useRef} from 'react';

export type GetCachedUserExists = ({
  applyEligibilityCheck,
  email,
  sessionTypeNames,
  siteId,
}: {
  applyEligibilityCheck?: boolean;
  email: string;
  sessionTypeNames?: string[];
  siteId?: string;
}) => Promise<AxiosResponse<UserExistsResponse, any>>;

export type GetCachedUserExistsStatus = ({
  email,
}: {
  email: string;
}) => Promise<AxiosResponse<UserAccountProfileExistResponse, any>>;

export const useGetCachedUserExists = () => {
  const cachedUserExists = useRef<{email: string; promise: Promise<AxiosResponse<UserExistsResponse, any>>} | null>(
    null
  );
  const cachedUserExistsStatus = useRef<{
    email: string;
    promise: Promise<AxiosResponse<UserAccountProfileExistResponse, any>>;
  } | null>(null);

  const getUserExistsStatusFromCacheOrApi: GetCachedUserExistsStatus = useCallback(async ({email}) => {
    if (cachedUserExistsStatus.current && cachedUserExistsStatus.current.email === email) {
      return await cachedUserExistsStatus.current.promise;
    }
    const checkUserStatusPromise = getUserAccountProfileExistStatus({email});
    cachedUserExistsStatus.current = {email, promise: checkUserStatusPromise};
    return await checkUserStatusPromise;
  }, []);

  const getUserExistsAndEligibilityFromCacheOrApi: GetCachedUserExists = useCallback(
    async ({applyEligibilityCheck = true, email, sessionTypeNames, siteId}) => {
      if (cachedUserExists.current && cachedUserExists.current.email === email) {
        return await cachedUserExists.current.promise;
      }
      const checkUserPromise = getUserExistsAndEligibility({applyEligibilityCheck, email, sessionTypeNames, siteId});
      cachedUserExists.current = {email, promise: checkUserPromise};
      return await checkUserPromise;
    },
    []
  );

  return {
    getCachedUserExists: getUserExistsAndEligibilityFromCacheOrApi,
    getCachedUserExistsStatus: getUserExistsStatusFromCacheOrApi,
  };
};
